import { useNavigate } from 'react-router'
import { Bell, ShoppingBag, RefreshCw, CheckCheck } from 'lucide-react'
import { useNotifications, useMarkAsRead, useMarkAllAsRead } from '@/lib/hooks'
import type { Notification } from '@/lib/hooks'
import {
  formatTransactionCreatedMessage,
  formatRecurringProcessedMessage,
  type TransactionCreatedData,
  type RecurringProcessedData,
} from '@/lib/notifications/format'

function formatRelativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = now - then

  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'たった今'
  if (minutes < 60) return `${minutes}分前`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}時間前`

  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}日前`

  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `${weeks}週間前`

  const months = Math.floor(days / 30)
  return `${months}ヶ月前`
}

function getNotificationMessage(notification: Notification): string {
  if (notification.type === 'transaction_created') {
    return formatTransactionCreatedMessage(notification.data as TransactionCreatedData)
  }

  if (notification.type === 'recurring_processed') {
    return formatRecurringProcessedMessage(notification.data as RecurringProcessedData)
  }

  return '新しい通知'
}

function NotificationIcon({ type }: { type: Notification['type'] }) {
  if (type === 'recurring_processed') {
    return (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400">
        <RefreshCw className="size-4" />
      </div>
    )
  }
  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400">
      <ShoppingBag className="size-4" />
    </div>
  )
}

type NotificationListProps = {
  onClose: () => void
}

export function NotificationList({ onClose }: NotificationListProps) {
  const navigate = useNavigate()
  const { data: notifications = [] } = useNotifications()
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()

  const hasUnread = notifications.some((n) => !n.is_read)

  const handleClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead.mutate(notification.id)
    }
    if (notification.transaction_id) {
      onClose()
      navigate(`/transactions/${notification.transaction_id}`)
    }
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-sm text-muted-foreground">
        <Bell className="mb-2 size-8 opacity-30" />
        通知はありません
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {hasUnread && (
        <div className="flex justify-end border-b px-4 py-2">
          <button
            type="button"
            onClick={() => markAllAsRead.mutate()}
            disabled={markAllAsRead.isPending}
            className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <CheckCheck className="size-3.5" />
            すべて既読にする
          </button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto" data-no-drag>
        {notifications.map((notification) => (
          <button
            key={notification.id}
            type="button"
            onClick={() => handleClick(notification)}
            className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent"
          >
            <NotificationIcon type={notification.type} />
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-snug">{getNotificationMessage(notification)}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {formatRelativeTime(notification.created_at)}
              </p>
            </div>
            {!notification.is_read && (
              <div className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

